/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { logger } from "@/lib/utils/logger";
import type { TestBypassResult } from "./test-mode";
import { getTestBypassConfig } from "./test-mode";
import type { TestUser } from "./test-users";
import { DEFAULT_TEST_USERS } from "./test-users";

async function getHeaders(): Promise<{
  get: (key: string) => string | null;
} | null> {
  if (typeof window !== "undefined") {
    return null;
  }
  try {
    const { headers } = await import("next/headers");
    const headerList = await headers();
    return headerList;
  } catch (error) {
    logger.warn("Could not import next/headers:", error);
    return null;
  }
}

export async function detectServerSideTestBypass(): Promise<TestBypassResult> {
  const config = getTestBypassConfig();
  const securityWarnings: string[] = [];

  if (!config.enabled) {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings,
    };
  }

  try {
    const headerList = await getHeaders();

    if (!headerList) {
      // Fallback to environment-based detection if headers not available
      // NOTE: Skip forced bypass when using real Clerk auth
      if (
        process.env.E2E_TEST_MODE === "true" &&
        process.env.USE_REAL_CLERK_AUTH !== "true"
      ) {
        const userId =
          (config.userIdOverride ?? DEFAULT_TEST_USERS.owner?.id) ||
          "660e8400-e29b-41d4-a716-446655440001";
        const orgId =
          (config.organizationIdOverride ??
            DEFAULT_TEST_USERS.owner?.organizationId) ||
          "550e8400-e29b-41d4-a716-446655440001";

        logger.info(
          "🧪 [TEST MODE] Server-side test bypass forced via environment (no headers)",
          {
            userId,
            orgId,
            source: "forced",
          },
        );

        return {
          isActive: true,
          source: "forced",
          user: DEFAULT_TEST_USERS.owner || DEFAULT_TEST_USERS.admin,
          organizationId: orgId,
          config,
          securityWarnings,
        };
      }

      return {
        isActive: false,
        source: "disabled",
        config,
        securityWarnings: ["Headers not available"],
      };
    }

    // Check for explicit test bypass headers (from middleware)
    const explicitBypass = headerList.get("x-test-bypass-auth") === "1";
    const userIdHeader = headerList.get("x-test-user-id");
    const orgIdHeader = headerList.get("x-test-org-id");
    const roleHeader = headerList.get("x-test-user-role");

    if (explicitBypass) {
      const userId =
        userIdHeader ?? config.userIdOverride ?? DEFAULT_TEST_USERS.owner.id;
      const orgId =
        orgIdHeader ??
        config.organizationIdOverride ??
        DEFAULT_TEST_USERS.owner.organizationId;
      const role = roleHeader ?? config.roleOverride ?? "owner";

      const user: TestUser = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        ...DEFAULT_TEST_USERS.owner,
        id: userId,
        organizationId: orgId,
        role,
      };

      logger.info("🧪 [TEST MODE] Server-side test bypass active via headers", {
        userId,
        orgId,
        role,
        source: "headers",
      });

      return {
        isActive: true,
        source: "headers",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }

    // Check for forced test mode (CI/E2E environments)
    // NOTE: As of Jan 2026, we use REAL Clerk authentication for E2E tests.
    // The E2E_TEST_MODE flag should NOT force bypass when USE_REAL_CLERK_AUTH is set.
    // This allows tests to use real Clerk sessions while still enabling test-specific behaviors.
    if (
      process.env.E2E_TEST_MODE === "true" &&
      process.env.USE_REAL_CLERK_AUTH !== "true"
    ) {
      const userId = config.userIdOverride ?? DEFAULT_TEST_USERS.owner.id;
      const orgId =
        config.organizationIdOverride ??
        DEFAULT_TEST_USERS.owner.organizationId;

      logger.info(
        "🧪 [TEST MODE] Server-side test bypass forced via environment",
        {
          userId,
          orgId,
          source: "forced",
        },
      );

      return {
        isActive: true,
        source: "forced",
        user: DEFAULT_TEST_USERS.owner,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }
  } catch (error) {
    logger.error("[TEST MODE] Error detecting server-side test bypass:", error);
    securityWarnings.push("Failed to read headers for test bypass detection");
  }

  return {
    isActive: false,
    source: "disabled",
    config,
    securityWarnings,
  };
}

export function detectClientSideTestBypass(): TestBypassResult {
  const config = getTestBypassConfig();
  const securityWarnings: string[] = [];

  if (!config.enabled) {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings,
    };
  }

  if (typeof window === "undefined") {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings: ["Window not available on server"],
    };
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hasTestBypass =
      urlParams.has("test-bypass") && urlParams.get("test-bypass") === "true";

    if (hasTestBypass) {
      const userId =
        urlParams.get("test-user-id") ??
        config.userIdOverride ??
        DEFAULT_TEST_USERS.owner.id;
      const orgId =
        urlParams.get("test-org-id") ??
        config.organizationIdOverride ??
        DEFAULT_TEST_USERS.owner.organizationId;
      const role =
        urlParams.get("test-user-role") ?? config.roleOverride ?? "owner";

      const user: TestUser = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        ...DEFAULT_TEST_USERS.owner,
        id: userId,
        organizationId: orgId,
        role,
      };

      logger.info("🧪 [TEST MODE] Client-side test bypass active via URL", {
        userId,
        orgId,
        role,
        source: "url",
        url: window.location.href,
      });

      return {
        isActive: true,
        source: "url",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }

    // Check for test bypass globals (from Playwright/E2E)
    const hasTestGlobals = window.__TEST_BYPASS_ENABLED;
    if (hasTestGlobals) {
      const userId = window.__TEST_USER_ID ?? DEFAULT_TEST_USERS.owner.id;
      const orgId =
        window.__TEST_ORG_ID ?? DEFAULT_TEST_USERS.owner.organizationId;
      const roleFromGlobal =
        window.__TEST_USER_ROLE ??
        config.roleOverride ??
        DEFAULT_TEST_USERS.owner.role;

      // Build user object with role from globals
      const user = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        ...DEFAULT_TEST_USERS.owner,
        id: userId,
        organizationId: orgId,
        role: roleFromGlobal,
      };

      // Override role if provided in globals (preserve original case, normalizeRole will handle it)
      if (window.__TEST_USER_ROLE) {
        user.role = roleFromGlobal;
      }

      logger.info("🧪 [TEST MODE] Client-side test bypass active via globals", {
        userId,
        orgId,
        role: user.role,
        source: "environment",
      });

      return {
        isActive: true,
        source: "environment",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }
  } catch (error) {
    logger.error("[TEST MODE] Error detecting client-side test bypass:", error);
    securityWarnings.push("Failed to parse URL for test bypass detection");
  }

  return {
    isActive: false,
    source: "disabled",
    config,
    securityWarnings,
  };
}

export function detectMiddlewareTestBypass(request: Request): TestBypassResult {
  const config = getTestBypassConfig();
  const securityWarnings: string[] = [];

  if (!config.enabled) {
    return {
      isActive: false,
      source: "disabled",
      config,
      securityWarnings,
    };
  }

  try {
    const url = new URL(request.url);
    const hasTestBypass =
      url.searchParams.has("test-bypass") &&
      url.searchParams.get("test-bypass") === "true";

    if (hasTestBypass) {
      const userId =
        url.searchParams.get("test-user-id") ??
        config.userIdOverride ??
        DEFAULT_TEST_USERS.owner.id;
      const orgId =
        url.searchParams.get("test-org-id") ??
        config.organizationIdOverride ??
        DEFAULT_TEST_USERS.owner.organizationId;
      const role =
        url.searchParams.get("test-user-role") ??
        config.roleOverride ??
        "owner";

      const user: TestUser = Object.values(DEFAULT_TEST_USERS).find(
        (u) => u.id === userId,
      ) ?? {
        ...DEFAULT_TEST_USERS.owner,
        id: userId,
        organizationId: orgId,
        role,
      };

      logger.info("🧪 [TEST MODE] Middleware test bypass active", {
        userId,
        orgId,
        role,
        source: "url",
        url: request.url,
      });

      return {
        isActive: true,
        source: "url",
        user,
        organizationId: orgId,
        config,
        securityWarnings,
      };
    }

    // Check for forced test mode
    // NOTE: Skip forced bypass when using real Clerk auth
    if (
      process.env.E2E_TEST_MODE === "true" &&
      process.env.USE_REAL_CLERK_AUTH !== "true"
    ) {
      return {
        isActive: true,
        source: "forced",
        user: DEFAULT_TEST_USERS.owner,
        organizationId: DEFAULT_TEST_USERS.owner.organizationId,
        config,
        securityWarnings,
      };
    }
  } catch (error) {
    logger.error("[TEST MODE] Error detecting middleware test bypass:", error);
    securityWarnings.push(
      "Failed to parse request URL for test bypass detection",
    );
  }

  return {
    isActive: false,
    source: "disabled",
    config,
    securityWarnings,
  };
}
